/**
 * starterTableGroups — pure list-building for the VQE starter canvas's table
 * picker (Task 4, §3.2 S9). Reads the schema snapshot's table map directly
 * rather than `useTableSuggestions` (which requires a resolver `scope` that
 * doesn't exist on a blank buffer — see `useSchemaSuggestions.ts:91-127`), and
 * groups the result to feed `CommandPickerPopover`'s `CommandPickerGroup[]`
 * shape verbatim.
 *
 * `buildStarterSelect` writes the table through `formatCatalogTableRef` — the
 * same catalog-spelling helper the FROM add-table flow emits through — so the
 * seeded `SELECT * FROM <table>` text needs no new quoting logic.
 */
import type { SqlEngine } from '../../../sql-grammar/model';
import type { SchemaSnapshot, Table } from '../../../sql-resolver/types/schema';
import {
  formatCatalogTableRef,
  tableSuggestionKey,
  tableSuggestionLabel,
} from '../edit/edits/fromItemHelpers';

export type StarterTableItem = {
  /** `tableSuggestionKey(table)` — stable id reported by the picker's onSelect. */
  readonly key: string;
  /** Search haystack cmdk filters on: the label (`${schema}.${name}`), lowercased. */
  readonly value: string;
  /** `tableSuggestionLabel(table)` — engine-aware qualified display name. */
  readonly label: string;
  readonly table: Table;
};

export type StarterTableGroup = {
  readonly heading?: string;
  readonly items: readonly StarterTableItem[];
};

function toStarterItem(table: Table): StarterTableItem {
  const label = tableSuggestionLabel(table);
  return {
    key: tableSuggestionKey(table),
    // The label, so two dotted names (`"a.b".c`, `a."b.c"`) stay two cmdk items.
    value: label.toLowerCase(),
    label,
    table,
  };
}

function byUnqualifiedName(a: Table, b: Table): number {
  return a.qualified.name.localeCompare(b.qualified.name);
}

/**
 * Groups the snapshot's tables by schema when the database has MORE THAN ONE
 * distinct schema (heading = schema name, items sorted A→Z by the unqualified
 * name); a single flat group (no heading) otherwise. Returns `[]` for a null
 * snapshot or a snapshot with zero tables — the caller renders its own honest
 * empty-schema message in that case rather than an empty picker group.
 */
export function starterTableGroups(
  snapshot: SchemaSnapshot | null,
): readonly StarterTableGroup[] {
  if (!snapshot) return [];
  const tables = Array.from(snapshot.tables.values());
  if (tables.length === 0) return [];

  const bySchema = new Map<string, Table[]>();
  for (const table of tables) {
    const schemaKey = table.qualified.schema ?? '';
    const bucket = bySchema.get(schemaKey);
    if (bucket) bucket.push(table);
    else bySchema.set(schemaKey, [table]);
  }

  if (bySchema.size <= 1) {
    return [{ items: tables.slice().sort(byUnqualifiedName).map(toStarterItem) }];
  }

  return Array.from(bySchema.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((schemaKey) => ({
      heading: schemaKey.length > 0 ? schemaKey : undefined,
      items: (bySchema.get(schemaKey) ?? []).slice().sort(byUnqualifiedName).map(toStarterItem),
    }));
}

/** `SELECT * FROM <table>` seed text for the starter canvas's table pick. */
export function buildStarterSelect(table: Table, engine: SqlEngine): string {
  return `SELECT * FROM ${formatCatalogTableRef(table, engine)}`;
}
